import React, { useState } from 'react';
import {
  FileText,
  BookOpen,
  Users,
  Zap,
  MessageSquare,
  GitBranch,
  CheckCircle,
  Circle,
  ArrowRight,
  AlertCircle,
  Loader
} from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import Analysis from './Analysis';
import TerminologyAnalysis from '../components/Analysis/TerminologyAnalysis';
import StakeholdersSystemsTab from '../components/Analysis/StakeholdersSystemsTab';
import SOPGenerationTab from '../components/Analysis/SOPGenerationTab';
import Interviews from './Interviews';
import ChangeDetection from './ChangeDetection';
import ProcessMaps from './ProcessMaps';

const SOPBuilder: React.FC = () => {
  const { currentProject } = useProject();
  const [activeTab, setActiveTab] = useState('review-summaries');
  const [analysisResults, setAnalysisResults] = React.useState<any>(null);

  // Load analysis results function
  const loadAnalysisResults = React.useCallback(async () => {
    if (!currentProject) return;
    try {
      const api = (await import('../api')).default;
      const response = await api.get(`/api/analysis/results/${currentProject.id}`);
      setAnalysisResults(response.data);
    } catch (error) {
      console.error('Failed to load analysis results:', error);
    }
  }, [currentProject]);

  // Load analysis results on mount and when project changes
  React.useEffect(() => {
    if (currentProject) {
      loadAnalysisResults();
    }
  }, [currentProject, loadAnalysisResults]);

  // Auto-refresh analysis results every 30 seconds to detect new data
  React.useEffect(() => {
    if (!currentProject) return;

    const interval = setInterval(() => {
      loadAnalysisResults();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [currentProject, loadAnalysisResults]);


  // Listen for process map updates to refresh synthesis/stakeholder data
  React.useEffect(() => {
    const handleProcessMapsUpdated = () => {
      console.log('🔄 [SOPBuilder] Process map updated, refreshing analysis results...');
      loadAnalysisResults();
    };

    window.addEventListener('process-maps-updated', handleProcessMapsUpdated as EventListener);

    return () => {
      window.removeEventListener('process-maps-updated', handleProcessMapsUpdated as EventListener);
    };
  }, [loadAnalysisResults]);

  const getStepStatus = (stepId: string) => {
    if (!analysisResults) return 'locked';

    switch (stepId) {
      case 'review-summaries':
        return analysisResults?.document_analyses?.length > 0 ? 'completed' : 'available';

      case 'approve-terminology':
        return analysisResults?.process_synthesis ? 'available' : 'locked';

      case 'approve-stakeholders':
        return analysisResults?.process_synthesis ? 'available' : 'locked';

      case 'validate-process-map':
        return analysisResults?.process_synthesis ? 'available' : 'locked';

      case 'generate-sop':
        return analysisResults?.process_synthesis ? 'available' : 'locked';

      case 'fill-gaps':
        return analysisResults?.sop ? 'available' : 'locked';

      case 'update-change':
        return analysisResults?.sop ? 'available' : 'locked';

      default:
        return 'locked';
    }
  };

  const getNextStepSuggestion = () => {
    if (!analysisResults?.document_analyses?.length) {
      return { step: 'review-summaries', message: 'Start by analyzing your documents' };
    }
    if (!analysisResults?.process_synthesis) {
      return { step: 'review-summaries', message: 'Generate Process Synthesis from Document Summaries tab' };
    }
    if (!analysisResults?.sop) {
      return { step: 'validate-process-map', message: 'Validate and generate your Process Map' };
    }
    return null;
  };

  const tabs = [
    { id: 'review-summaries', label: '1. Review Document Summaries', icon: FileText, stepNum: '1' },
    { id: 'approve-terminology', label: '2. Approve Terminology', icon: BookOpen, stepNum: '2' },
    { id: 'approve-stakeholders', label: '3. Approve Stakeholders and Systems', icon: Users, stepNum: '3' },
    { id: 'validate-process-map', label: '4. Validate Process Map', icon: GitBranch, stepNum: '4' },
    { id: 'generate-sop', label: '5. Generate SOP', icon: Zap, stepNum: '5' },
    { id: 'fill-gaps', label: '6. Fill Gaps', icon: MessageSquare, stepNum: '6' },
    { id: 'update-change', label: '7. Update/Change', icon: GitBranch, stepNum: '7' }
  ];

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Please select a project to use SOP Builder</p>
        </div>
      </div>
    );
  }

  const nextStep = getNextStepSuggestion();

  return (
    <div className="space-y-6">
      {nextStep && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-900 font-medium">Next Step</p>
            <p className="text-blue-700 text-sm">{nextStep.message}</p>
          </div>
          {nextStep.step !== activeTab && (
            <button
              onClick={() => setActiveTab(nextStep.step)}
              className="ml-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 whitespace-nowrap"
            >
              <span>Go to Step</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <nav className="flex space-x-2 p-2 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const status = getStepStatus(tab.id);
            const isLocked = status === 'locked';
            const isCompleted = status === 'completed';

            // Check if synthesis is being generated (documents analyzed but no synthesis yet)
            const hasDocs = analysisResults?.document_analyses?.length > 0;
            const hasSynthesis = analysisResults?.process_synthesis;
            const hasSOP = analysisResults?.sop;
            const isWaitingForSynthesis = hasDocs && !hasSynthesis && ['approve-terminology', 'approve-stakeholders', 'validate-process-map', 'generate-sop'].includes(tab.id);
            const isWaitingForSOP = hasSynthesis && !hasSOP && ['fill-gaps', 'update-change'].includes(tab.id);

            return (
              <button
                key={tab.id}
                onClick={() => !isLocked && setActiveTab(tab.id)}
                disabled={isLocked}
                title={
                  isWaitingForSynthesis
                    ? 'Process Synthesis is being generated. This will unlock automatically.'
                    : isWaitingForSOP
                    ? 'Waiting for SOP generation. Generate SOP from Step 5 to unlock.'
                    : ''
                }
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap relative ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : isLocked
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed opacity-60'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : isLocked ? (
                  isWaitingForSynthesis || isWaitingForSOP ? (
                    <Loader className="w-4 h-4 animate-spin text-blue-500" />
                  ) : (
                    <Circle className="w-4 h-4" />
                  )
                ) : (
                  <Icon className="w-4 h-4" />
                )}
                <span>{tab.label}</span>
                {isLocked && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ml-1 ${
                    isWaitingForSynthesis || isWaitingForSOP
                      ? 'bg-blue-100 text-blue-700 animate-pulse'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {isWaitingForSynthesis
                      ? 'Generating Synthesis...'
                      : isWaitingForSOP
                      ? 'Waiting for SOP...'
                      : 'Complete previous steps'
                    }
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'review-summaries' && (
        <Analysis />
      )}

      {activeTab === 'approve-terminology' && (
        <>
          {!analysisResults?.process_synthesis ? (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
              <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-yellow-900 mb-2">Process Synthesis Required</h3>
              <p className="text-yellow-700 mb-4">
                Terminology data is generated after Process Synthesis is complete.
              </p>
              <p className="text-yellow-600 text-sm mb-6">
                Please go to <strong>Step 1: Review Document Summaries</strong> → Click on <strong>Process Synthesis</strong> tab → Generate the synthesis to unlock this step.
              </p>
              <button
                onClick={() => setActiveTab('review-summaries')}
                className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors inline-flex items-center space-x-2"
              >
                <ArrowRight className="w-4 h-4" />
                <span>Go to Document Summaries</span>
              </button>
            </div>
          ) : (
            <TerminologyAnalysis
              analysisResults={analysisResults}
              onRefresh={async () => {
                if (!currentProject) return;
                try {
                  const api = (await import('../api')).default;
                  const response = await api.get(`/api/analysis/results/${currentProject.id}`);
                  setAnalysisResults(response.data);
                } catch (error) {
                  console.error('Failed to refresh analysis results:', error);
                }
              }}
            />
          )}
        </>
      )}

      {activeTab === 'approve-stakeholders' && (
        <>
          {!analysisResults?.process_synthesis ? (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
              <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-yellow-900 mb-2">Process Synthesis Required</h3>
              <p className="text-yellow-700 mb-4">
                Stakeholders & Systems data is generated after Process Synthesis is complete.
              </p>
              <p className="text-yellow-600 text-sm mb-6">
                Please go to <strong>Step 1: Review Document Summaries</strong> → Click on <strong>Process Synthesis</strong> tab → Generate the synthesis to unlock this step.
              </p>
              <button
                onClick={() => setActiveTab('review-summaries')}
                className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors inline-flex items-center space-x-2"
              >
                <ArrowRight className="w-4 h-4" />
                <span>Go to Document Summaries</span>
              </button>
            </div>
          ) : (
            <StakeholdersSystemsTab
              synthesis={analysisResults?.process_synthesis}
              isAnalyzing={false}
              analysisProgress={null}
            />
          )}
        </>
      )}

      {activeTab === 'validate-process-map' && (
        <ProcessMaps />
      )}

      {activeTab === 'generate-sop' && (
        <SOPGenerationTab
          analysisResults={analysisResults}
          loadAnalysisResults={loadAnalysisResults}
          isAnalyzing={false}
          analysisProgress={null}
          globalComplianceScore={0}
          onRefresh={loadAnalysisResults}
        />
      )}

      {activeTab === 'fill-gaps' && (
        <Interviews />
      )}

      {activeTab === 'update-change' && (
        <ChangeDetection />
      )}
    </div>
  );
};

export default SOPBuilder;
